import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { RagBadge } from "@/components/StatusBadge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Search, Ban } from "lucide-react";

export default function AdminCarriers() {
  const [carriers, setCarriers] = useState(null);
  const [search, setSearch] = useState("");
  const [compliance, setCompliance] = useState("all");
  const navigate = useNavigate();

  useEffect(() => {
    const t = setTimeout(() => {
      api.get("/admin/subcontractors", { params: { search, compliance } })
        .then((r) => setCarriers(r.data))
        .catch((e) => toast.error(formatApiError(e)));
    }, 250);
    return () => clearTimeout(t);
  }, [search, compliance]);

  return (
    <div className="space-y-6" data-testid="admin-carriers-page">
      <div>
        <h1 className="font-heading text-2xl font-extrabold text-navy sm:text-3xl">Carriers</h1>
        <p className="mt-1 text-sm text-muted-foreground">Search, filter and review every subcontractor and carrier.</p>
      </div>

      <div className="flex flex-col gap-3 sm:flex-row">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="carrier-search-input" className="pl-9" placeholder="Search by name, ABN or email…"
            value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <Select value={compliance} onValueChange={setCompliance}>
          <SelectTrigger data-testid="compliance-filter-select" className="w-full sm:w-52">
            <SelectValue placeholder="Compliance status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all" data-testid="filter-all">All statuses</SelectItem>
            <SelectItem value="green" data-testid="filter-green">Green</SelectItem>
            <SelectItem value="amber" data-testid="filter-amber">Amber</SelectItem>
            <SelectItem value="red" data-testid="filter-red">Red</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="overflow-x-auto rounded-md border border-border bg-card">
        <table className="w-full min-w-[760px] text-left text-sm" data-testid="carriers-table">
          <thead>
            <tr className="border-b border-border bg-muted/60 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              <th className="px-5 py-3">Company</th>
              <th className="px-5 py-3">ABN</th>
              <th className="px-5 py-3">Contact</th>
              <th className="px-5 py-3">Compliance</th>
              <th className="px-5 py-3">Pending docs</th>
              <th className="px-5 py-3">Registered</th>
            </tr>
          </thead>
          <tbody>
            {!carriers ? (
              <tr><td colSpan={6} className="px-5 py-8 text-center text-muted-foreground">Loading carriers…</td></tr>
            ) : carriers.length === 0 ? (
              <tr><td colSpan={6} className="px-5 py-8 text-center text-muted-foreground" data-testid="carriers-empty">No carriers match your filters.</td></tr>
            ) : carriers.map((c) => (
              <tr key={c.id} data-testid={`carrier-row-${c.id}`}
                className="cursor-pointer border-b border-border transition-colors duration-150 last:border-b-0 hover:bg-slate-50"
                onClick={() => navigate(`/admin/carriers/${c.id}`)}>
                <td className="px-5 py-3.5">
                  <div className="flex items-center gap-2 font-semibold text-navy">
                    {c.legal_business_name || c.business_name}
                    {c.suspended && (
                      <span data-testid={`suspended-badge-${c.id}`} className="inline-flex items-center gap-1 rounded-full border border-red-200 bg-red-50 px-2 py-0.5 text-[10px] font-bold uppercase text-red-700">
                        <Ban className="h-3 w-3" /> Suspended
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-muted-foreground">{c.trading_name || c.email}</div>
                </td>
                <td className="px-5 py-3.5 font-mono text-xs">{c.abn || "—"}</td>
                <td className="px-5 py-3.5">
                  <div>{c.contact_person || "—"}</div>
                  <div className="text-xs text-muted-foreground">{c.phone || c.email}</div>
                </td>
                <td className="px-5 py-3.5"><RagBadge status={c.compliance_status} testid={`carrier-rag-${c.id}`} /></td>
                <td className="px-5 py-3.5" data-testid={`carrier-pending-${c.id}`}>
                  {c.pending_docs > 0
                    ? <span className="font-semibold text-amber-600">{c.pending_docs} to review</span>
                    : <span className="text-muted-foreground">0</span>}
                </td>
                <td className="px-5 py-3.5 text-xs text-muted-foreground">
                  {c.created_at ? new Date(c.created_at).toLocaleDateString("en-AU") : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
